import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';
import EmailPasswordForm from './EmailPasswordForm';
import { Mail, ChevronDown, ChevronUp } from 'lucide-react';

export default function LoginPage() {
  const { isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showEmailForm, setShowEmailForm] = useState(false);

  const errorMessage = searchParams.get('error');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Clear any previous errors when component mounts
    clearError();
  }, [clearError]);

  const displayError = error || (errorMessage ? decodeURIComponent(errorMessage) : null);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#CC0202' }}>
            <span className="text-white text-2xl font-bold">HN</span>
          </div>
        </div>

        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Welcome to HarbourNest
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Your home command center
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {displayError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {displayError === 'auth_failed' ? 'Authentication failed. Please try again.' : displayError}
            </div>
          )}

          <div className="space-y-4">
            {/* Google Sign-In (Recommended) */}
            <GoogleSignInButton />

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>

            {/* Email/Password Toggle */}
            <button
              onClick={() => setShowEmailForm(!showEmailForm)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              <Mail className="w-5 h-5" />
              <span>Continue with Email</span>
              {showEmailForm ? (
                <ChevronUp className="w-4 h-4 ml-auto" />
              ) : (
                <ChevronDown className="w-4 h-4 ml-auto" />
              )}
            </button>

            {/* Email/Password Form (Expandable) */}
            {showEmailForm && (
              <div className="mt-4 pt-4 border-t">
                <EmailPasswordForm mode="login" />
                <p className="mt-4 text-center text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/register" className="font-medium hover:underline" style={{ color: '#CC0202' }}>
                    Create one
                  </Link>
                </p>
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-xs text-gray-500">
            By signing in, you agree to our{' '}
            <a href="#" className="underline hover:text-gray-700">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="underline hover:text-gray-700">Privacy Policy</a>
          </div>
        </div>
      </div>
    </div>
  );
}
